<template>
  <v-card v-if="$vuetify.breakpoint.xs">
    <v-card-text>This feature is only supported on devices with larger screen-sizes.</v-card-text>
  </v-card>
  <v-container
    v-else
    id="floor-plan-wrapper"
    fluid
    class="pa-0 pb-5"
    :style="'height: ' + calculatedInnerHeight + ';'"
  >
    <find-meal-period
      ref="findMealPeriod"
      v-model="showFindMealPeriod"
      :default-location="findMealPeriodUseDefaultLocation ? floorPlanData.location : undefined"
      @set-floor-plan-data="setFloorPlanData"
      @set-floor-plan-event-data="setFloorPlanEventData"
    />
    <api-errors :errors="errors" />
    <v-row no-gutters>
      <v-col
        cols="4"
        class="px-1"
      >
        <left-side-panel
          ref="leftSidePanel"
          v-bind="floorPlanData"
          :server-assignments-map="allServerAssignmentsMap"
          :table-config-id="(selectedTableConfiguration || {}).id"
          :auto-clear-reservation-status="(selectedTableConfiguration || {}).autoClearReservationStatus || false"
          :multi-select-mode="multiSelectMode"
          class="column-content-card"
          @multi-select-mode="multiSelectMode = $event"
          @add-table="selectedTables = $event"
          @add-tables="multiSelectMode = $event"
          @set-floor-plan-data="(locationId, mealPeriodId) => setFloorPlanData(locationsMap[locationId], floorPlanData.date, mealPeriodsMap[mealPeriodId])"
          @set-time-radar="floorPlanData.time = dbTimeToMinutes($event)[0]"
          @scroll-to-selected-reservation="scrollToSelectedTables"
          @refresh-server-assignments="refreshServerAssignments(floorPlanData.mealPeriod.id, floorPlanData.date)"
          @refresh-meal-period="refreshMealPeriod"
          @refresh-floor-plan="refreshFloorPlan"
          @refresh-tables="refreshTableConfiguration"
        />
      </v-col>
      <v-col
        id="floor-plan-right-side-column"
        cols="8"
        class="px-1"
      >
        <v-card
          id="right-side-container"
          class="pa-0 column-content-card"
        >
          <div id="floor-plan-header">
            <floor-plan-header
              v-bind="{
                multiSelectMode,
                ...floorPlanData,
                mealPeriods,
                tableConfigurations
              }"
              :for-event="floorPlanData.forEvent"
              :time-radar-expanded="showTimeRadar"
              :table-configuration="selectedTableConfiguration"
              @enable-multi-select="multiSelectMode = !multiSelectMode"
              @print="printCanvas"
              @set-table-config="(tableConfigIndex) => {
                selectedFloorPlan = tableConfigIndex
                const {forEvent, eventLocationTableConfigMap: map} = floorPlanData || {}
                if (forEvent) {
                  for (let eventLocation in map) {
                    if (map[eventLocation] === tableConfigurations[tableConfigIndex].id) {
                      $set(floorPlanData, 'location', locationsMap[eventLocation])
                      break
                    }
                  }
                }
              }"
              @refresh="refreshTableConfiguration"
              @find-meal-period="(useLocation) => {
                showFindMealPeriod = true
                findMealPeriodUseDefaultLocation = useLocation
              }"
              @set-meal-period="setFloorPlanData(floorPlanData.location, floorPlanData.date, $event)"
              @toggle-time-radar="showTimeRadar = !showTimeRadar"
            />
            <v-expand-transition>
              <div
                v-show="showTimeRadar"
                class="time-radar-wrapper elevation-1"
              >
                <time-radar
                  v-model="floorPlanData.time"
                  :meal-period="floorPlanData.mealPeriod"
                  :steps="Object.keys(floorPlanMapResult)"
                  class="py-2"
                  @change="$set($refs.leftSidePanel, 'selectedReservation', undefined)"
                />
              </div>
            </v-expand-transition>
          </div>
          <v-card>
            <div
              class="d-flex justify-center"
              :class="{'pt-2': multiSelectMode}"
              :style="{transition: 'all 0.2s ease-in-out'}"
            >
              <floor-plan-canvas
                ref="canvas"
                style="width: 100%"
                v-bind="{tables, shapes, labels, icons, serverAssignmentsMap, reservationXRay, multiSelectMode}"
                :auto-clear-reservation-status="(selectedTableConfiguration || {}).autoClearReservationStatus || false"
                :location-id="(floorPlanData.location || {}).id"
                :canvas-height="(selectedTableConfiguration || {}).canvasHeight"
                :canvas-width="(selectedTableConfiguration || {}).canvasWidth"
                :show-server-assignment="(selectedTableConfiguration || {}).showServerAssignment"
                is-static
                :show-next-reservation="!(floorPlanData.time && showTimeRadar)"
                :reservations="floorPlanMapResult"
                :show-reservations-at="showTimeRadar ? floorPlanData.time : -1"
                :grid-density="0"
                disable-drag
                select-reservation
                :selected-reservation="getSelectedReservation()"
                :selected-open-tables="selectedTables"
                @select-reservation="selectReservation"
                @select-open-table="selectOpenTable"
                @update-element="updateElement"
              />
            </div>
            <v-fade-transition
              hide-on-leave
              group
            >
              <v-card-actions
                v-if="!!getSelectedReservation()"
                id="floor-plan-actions"
                key="floor-plan-members"
              >
                <selected-reservation-members
                  ref="selectedReservationMembers"
                  :reservation="getSelectedReservation()"
                  style="width: 100%;"
                  @refresh="$refs.leftSidePanel.refreshSelectedReservation()"
                />
              </v-card-actions>
              <v-card-actions
                v-else
                id="floor-plan-actions"
                key="floor-plan-buttons"
              >
                <floor-plan-data-footer
                  :event-id="(floorPlanData.event || {}).id"
                  :meal-period-id="mealPeriod.id"
                  :table-configuration-id="(selectedTableConfiguration || {}).id"
                />
              </v-card-actions>
            </v-fade-transition>
          </v-card>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import mealPeriodHelpers from './mixins/mealPeriodHelpers'
import LeftSidePanel from './leftSidePanel/LeftSidePanel'
import FloorPlanHeader from './FloorPlanHeader'
import TimeRadar from './TimeRadar'
import errorHandler from '@/ft-vue-core/mixins/errorHandler'
import ApiErrors from '@/ft-vue-core/components/ApiErrors'
import floorPlanTimeHelpers from './mixins/floorPlanTimeHelpers'
import floorPlanStyleHelpers from './mixins/floorPlanStyleHelpers'
import FindMealPeriod from './FindMealPeriod'
import SelectedReservationMembers from './SelectedReservationMembers'
import FloorPlanCanvas from '../configuration/tables/floorPlan/FloorPlanCanvas'
import FloorPlanDataFooter from './FloorPlanDataFooter'
import {deepClone} from '@/ft-vue-core/util/helpers'

const TIME_TENSES = ['Future', 'Past', 'Current']
function* refresher (prefix) {
  for (let i = 0; ; ++i) yield `${prefix}-${i}`
}
export default {
  name: 'FloorPlan',
  components: {LeftSidePanel, FloorPlanHeader, TimeRadar, ApiErrors, FindMealPeriod, SelectedReservationMembers, FloorPlanCanvas, FloorPlanDataFooter},
  mixins: [errorHandler, mealPeriodHelpers, floorPlanTimeHelpers, floorPlanStyleHelpers],
  data () {
    return {
      /** Forces refresh/reactivity on data contained in $refs */
      selectedTablesRefresher: refresher('selected-tables'),
      selectedTablesRefreshKey: 0,

      floorPlanData: {
        time: 0,
        date: 0,
        location: {},
        mealPeriod: {}
      },
      serverAssignmentsMap: {},
      allServerAssignmentsMap: {},
      tableConfigurations: [],
      selectedFloorPlan: undefined,
      mealPeriods: [],
      showFindMealPeriod: false,
      findMealPeriodUseDefaultLocation: false,
      showTimeRadar: false,
      reservationXRay: false,
      allLocations: [],
      allMealPeriods: [],

      floorPlanMapResult: {},
      multiSelectMode: false,
    }
  },
  computed: {
    mealPeriod () {
      return this.floorPlanData?.mealPeriod ?? {}
    },
    selectedTableConfiguration () {
      return this.tableConfigurations[this.selectedFloorPlan || 0]
    },
    icons () {
      const selectedFloorPlan = this.selectedFloorPlan ?? 0
      return this.tableConfigurations[selectedFloorPlan]?.icons || []
    },
    labels () {
      const selectedFloorPlan = this.selectedFloorPlan ?? 0
      return this.tableConfigurations[selectedFloorPlan]?.labels || []
    },
    shapes () {
      const selectedFloorPlan = this.selectedFloorPlan ?? 0
      return this.tableConfigurations[selectedFloorPlan]?.shapes || []
    },
    tables () {
      const selectedFloorPlan = this.selectedFloorPlan ?? 0
      return this.tableConfigurations[selectedFloorPlan]?.tables || []
    },
    tablesMap () {
      return this.tables.reduce((acc, t) => {
        acc[t.tableId] = t
        return acc
      }, {}) ?? {}
    },
    /**
     * Computed property get/set the `selectedTables` attribute of `leftSidePanel`
     * Forces reactivity using a custom key-refresher implementation
     * @see selectedTablesRefresher
     * @see selectedTablesRefreshKey
     */
    selectedTables: {
      get () {
        this.selectedTablesRefreshKey
        return this.$refs.leftSidePanel?.selectedTables
      },
      set (tables) {
        const ref = this.$refs.leftSidePanel
        if (ref) ref.setSelectedTables(deepClone(tables ?? []))
        this.selectedTablesRefreshKey = this.selectedTablesRefresher.next().value
      }
    },
    locationsMap () {
      return this.allLocations.reduce((acc, l) => {
        acc[l.id] = l
        return acc
      }, {}) ?? {}
    },
    mealPeriodsMap () {
      return this.allMealPeriods.reduce((acc, m) => {
        acc[m.id] = m
        return acc
      }, {}) ?? {}
    },
  },
  watch: {
    showTimeRadar: {
      handler (value) {
        if (!value) return
        const {nearestReservationTime} = this.$refs.canvas ?? {}
        if (nearestReservationTime) this.$set(this.floorPlanData, 'time', nearestReservationTime)
      },
      immediate: true
    }
  },
  beforeDestroy () {
    const [element] = document.getElementsByTagName('html')
    element.style['overflow-y'] = 'scroll'
  },
  mounted () {
    const [element] = document.getElementsByTagName('html')
    element.style['overflow-y'] = 'hidden'
    this.awaitingApiResponse = 6
    this.$ftAPI().get([
      {
        command: 'diningLocation',
        load: this.allLocations,
        loading: this.isLoading,
        afterLoad: () => this.awaitingApiResponse -= 1,
        error (e) {
          this.awaitingApiResponse -= 1
          this.errors.push(e.message)
        }
      },
      {
        command: 'diningMealPeriod',
        load: this.allMealPeriods,
        loading: this.isLoading,
        afterLoad: () => this.awaitingApiResponse -= 1,
        error (e) {
          this.awaitingApiResponse -= 1
          this.errors.push(e.message)
        }
      },
    ])
    this.$ftAPI().get({
      command: 'diningFindCurrentMealPeriod',
      loading: this.isLoading,
      afterLoad: (r) => {
        this.awaitingApiResponse -= 1
        const result = r?.[0] ?? {}
        const {dateTime: date, location, mealPeriod} = result
        // this.setFloorPlanData(location, date, mealPeriod)
        this.floorPlanData = {
          time: this.dbTimeToMinutes(result.dateTime)?.[0] ?? 0,
          date, location, mealPeriod
        }
        // this.$set(this.floorPlanData, 'time', this.dbTimeToMinutes(result.dateTime)?.[0] ?? 0)
        const commands = [
          {
            command: 'diningMealPeriod',
            load: this.mealPeriods,
            afterLoad: r => {
              this.awaitingApiResponse -= 1
              const result = r?.[0] ?? {}
              // this.$set(this.floorPlanData, 'mealPeriod', result)
              this.setFloorPlanData(location, date, result)
            },
            error: r => {
              this.awaitingApiResponse -= 1
              this.errors.push(r)
            },
            loading: this.isLoading,
            params: {
              children: true,
              include_fields: ['locationName', 'seatings'],
              filter_methods: TIME_TENSES.reduce((acc, e) => {
                acc[`includeIs${e}`] = [date]
                return acc
              }, {}),
              filter: [{field: 'locationId', operator: 'EQUAL', values: [this.floorPlanData.location?.id]}]
            }
          },
          {
            command: 'diningTableConfiguration',
            load: this.tableConfigurations,
            afterLoad: () => this.awaitingApiResponse -= 1,
            error (e) {
              this.errors.push(e.message)
              this.awaitingApiResponse -= 1
            },
            loading: this.isLoading,
            params: {
              children: true,
              filter: [
                {field: 'locationId', operator: 'EQUAL', values: [this.floorPlanData.location?.id]},
                {field: 'useFloorPlan', operator: 'EQUAL', values: [true]},
              ]
            }
          },
        ]
        this.$ftAPI().get(this.addResourceErrorHandler(commands))
        // this.refreshTableConfiguration()
        this.$ftAPI().get(this.addResourceErrorHandler(
          this.diningGetFloorPlanServerAssignmentsCommand(
            this.floorPlanData.mealPeriod.id,
            this.floorPlanData.date
          )
        ))
      },
      error: (err) => {
        this.awaitingApiResponse -= 1
        this.showFindMealPeriod = true
        this.$nextTick(() => this.$refs.findMealPeriod?.errors.push(err.message))
      }
    })
  },
  methods: {
    printCanvas () {
      this.$refs.canvas?.$refs.stage?.print()
    },
    updateElement (list, index, value) {
      this[list]?.splice(index, 1, value)
    },
    scrollVertical (location, timeout = 0) {
      const canvasContainer = document.querySelector('#floor-plan-right-side-column .column-content-card')
      if (location === 'bottom') this.$refs.selectedReservationMembers?.openReservees(), timeout = 200
      setTimeout(() => canvasContainer.scrollTo({top: location === 'bottom' ? 1000 : 0, behavior: 'smooth'}), timeout)
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    scrollHorizontal (location) {
      const canvasContainer = document.querySelector('.floor-plan-container')
      canvasContainer.scrollTo({left: location === 'right' ? 1000 : 0, behavior: 'smooth'})
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    scrollToSelectedTables (tables = []) {
      const floorPlanContainer = document.querySelector('.floor-plan-container')
      if (!floorPlanContainer) return // Save the cycles if something went wrong here
      let x = tables.reduce((acc, t) => {
        const table = this.tablesMap[t.locationTableId]
        if (table.x < acc || acc === -1) acc = table.x
        return acc
      }, -1) ?? 0
      floorPlanContainer.scrollTo({left: Math.max(x - 100, 0), behavior: 'smooth'})
    },
    selectReservation (reservation, tableIds) {
      this.$refs.leftSidePanel?.setSelectedReservation(reservation)
    },
    selectOpenTable (table) {
      const target = `${this.multiSelectMode ? 'add' : 'set'}SelectedTable`
      this.$refs.leftSidePanel?.[target](table)
    },
    getSelectedReservation () {
      return this.$refs.leftSidePanel?.selectedReservation
    },
    getSelectedTable () {
      return this.$refs.leftSidePanel?.selectedTable
    },
    setSelectedTables (tables) {
      const ref = this.$refs.leftSidePanel
      if (ref) {
        ref.selectedTables = deepClone(tables)
        this.selectedTablesRefreshKey = this.selectedTablesRefresher.next().value
      }
    },
    getSelectedTables () {
      return this.$refs.leftSidePanel?.selectedTables
    },
    setFloorPlanData (location, date, mealPeriod) {
      let floorPlanData = {}
      floorPlanData.location = location
      floorPlanData.date = date
      floorPlanData.mealPeriod = mealPeriod
      this.floorPlanData = deepClone(floorPlanData)
      this.showFindMealPeriod = false
      this.loadList()
    },
    setFloorPlanEventData (date, event) {
      let floorPlanData = {}
      const eventLocationTableConfigMap = event.locations?.reduce((acc, l) => {
        acc[l.locationId] = l.tableConfigId
        return acc
      }, {})
      const [location] = Object.keys(eventLocationTableConfigMap)?.map(id => this.locationsMap[id])
      Object.assign(floorPlanData, {location, date, event, eventLocationTableConfigMap, mealPeriod: {}, forEvent: true})
      this.floorPlanData = deepClone(floorPlanData)
      this.showFindMealPeriod = false
      this.loadList()
    },
    /**
     * Call to `diningGetFloorPlanReservationsMap` to get reservations to display
     * @param {Object} data Data for `diningGerFloorPlanReservationsMap`.
     * @see data REQUIRES: `mealPeriodId`, `date` and `locationId` parameters
     */
    getFloorPlanReservationsCommand (data, afterLoad) {
      const {mealPeriodId, locationId, date, eventId} = data
      return this.addResourceErrorHandler(
        {
          command: 'diningGetFloorPlanReservationsMap',
          load: r => this.floorPlanMapResult = r[0]?.result ?? {},
          afterLoad: (r) => afterLoad?.(r),
          params: {
            filter_methods: this.floorPlanData.forEvent
              ? {filterEventData: [eventId, locationId]}
              : {filterData: [mealPeriodId, locationId, date]}
          }
        }
      )
    },
    diningGetFloorPlanServerAssignmentsCommand (mealPeriodId, date) {
      return {
        command: 'diningGetFloorPlanServerAssignmentsMap',
        load: (r) => {
          this.serverAssignmentsMap = r[0]?.result ?? {}
          this.allServerAssignmentsMap = r[0]?.resultAll ?? {}
        },
        params: {
          filter_methods: this.floorPlanData.forEvent
            ? {filterEventDataAllServers: [this.floorPlanData.event.id]}
            : {filterDataAllServers: [mealPeriodId, date]}
        }
      }
    },
    getMealPeriodCommand () {
      const {date} = this
      return {
        command: 'diningMealPeriod',
        loading: this.isLoading,
        load: (r) => this.$set(this.floorPlanData, 'mealPeriod', r?.[0] ?? {}),
        params: {
          children: true,
          include_fields: ['locationName', 'seatings'],
          filter_methods: {
            filterServersByDate: [date],
            ...TIME_TENSES.reduce((acc, e) => {
              acc[`includeIs${e}`] = [date]
              return acc
            }, {})
          },
          filter: [{field: 'id', operator: 'EQUAL', values: [this.floorPlanData.mealPeriod.id]}]
        }
      }
    },
    getDiningLocationCommand () {
      return {
        command: 'diningLocation',
        loading: this.isLoading,
        load: (r) => this.$set(this.floorPlanData, 'location', r?.[0] ?? {}),
        params: {
          filter: [{field: 'id', operator: 'EQUAL', values: [this.floorPlanData.location?.id]}]
        }
      }
    },
    refreshTableConfiguration () {
      this.$ftAPI().get(
        this.getFloorPlanReservationsCommand({
          mealPeriodId: this.floorPlanData.mealPeriod.id,
          locationId: this.floorPlanData.location?.id,
          date: this.floorPlanData.date,
          eventId: this.floorPlanData.event?.id,
          isCurrent: this.floorPlanData.mealPeriod.isCurrent
        }, () => this.$refs.canvas.buildLocationTableMap?.(this.floorPlanData.location?.id)))
    },
    loadList () {
      const {date, location, mealPeriod, event} = this.floorPlanData ?? {}
      let tableConfigParams = {children: true, filter: [{field: 'useFloorPlan', operator: 'EQUAL', values: [true]}]}
      if (this.floorPlanData.forEvent) {
        const {eventLocationTableConfigMap: map} = this.floorPlanData || {}
        const locationIds = Object.keys(map)
        if (locationIds.length > 1) {
          tableConfigParams.filter.push({field: 'id', operator: 'IN', values: Object.values(map)})
        } else if (locationIds.length === 1) {
          tableConfigParams.filter.push({field: 'id', operator: 'EQUAL', values: Object.values(map)})
        }
      } else tableConfigParams.filter.push({field: 'locationId', operator: 'EQUAL', values: [location?.id]})
      const commands = [
        this.getDiningLocationCommand(),
        {
          command: 'diningMealPeriod',
          load: this.mealPeriods,
          loading: this.isLoading,
          params: {
            children: true,
            include_fields: ['locationName', 'seatings'],
            filter: [{field: 'locationId', operator: 'EQUAL', values: [this.floorPlanData.location?.id]}],
            filter_methods: TIME_TENSES.reduce((acc, e) => {
              acc[`includeIs${e}`] = [date]
              return acc
            }, {})
          }
        },
        {
          command: 'diningTableConfiguration',
          load: this.tableConfigurations,
          loading: this.isLoading,
          params: tableConfigParams
        },
        this.getFloorPlanReservationsCommand({
          mealPeriodId: mealPeriod.id,
          locationId: location.id,
          date,
          eventId: event?.id,
          isCurrent: mealPeriod.isCurrent
        })
      ]
      this.$ftAPI().get(this.addResourceErrorHandler(commands))
      // this.refreshFloorPlan() // TODO put this back and FIXME for events
    },
    refreshMealPeriod () {
      this.$ftAPI().get(this.addResourceErrorHandler(this.getMealPeriodCommand()))
    },
    refreshFloorPlan () {
      const {id: mealPeriodId, isCurrent} = this.floorPlanData.mealPeriod
      const [locationId, date, eventId] = [this.floorPlanData.location?.id, this.floorPlanData.date, this.floorPlanData.event?.id]
      this.refreshServerAssignments(mealPeriodId, date)
      this.$ftAPI().get(this.getFloorPlanReservationsCommand({
        locationId, mealPeriodId, date, eventId
      }, (r) => {
        const {result} = r?.[0]
        const [mealPeriodId, date] = [this.floorPlanData.mealPeriod.id, this.floorPlanData.date]
        if (!isCurrent) {
          this.$nextTick(() => {
            this.showTimeRadar = true
            this.floorPlanData.time = Object.keys(result)[0]
          })
        }
        // this.$set(this.floorPlanData, 'time', Object.keys(r?.[0].result)[0])
        this.refreshServerAssignments(mealPeriodId, date)
      }))
    },
    refreshServerAssignments (mealPeriodId, date) {
      this.$ftAPI().get(this.addResourceErrorHandler(
        this.diningGetFloorPlanServerAssignmentsCommand(mealPeriodId, date)
      ))
    },
    refreshReservationTables () {
      const [locationId, mealPeriodId, date, eventId] = [this.floorPlanData.location?.id, this.floorPlanData.mealPeriod.id, this.floorPlanData.date, this.floorPlanData.event?.id]
      this.$ftAPI().get(
        Object.assign(
          this.getFloorPlanReservationsCommand({locationId, mealPeriodId, date, eventId}),
          {afterLoad: () => this.$refs.canvas.buildLocationTableMap(this.floorPlanData.location?.id)}
        )
      )
    }
  }
}
</script>

<style scoped>
body::-webkit-scrollbar, #right-side-container::-webkit-scrollbar {
  display: none
}
body, #right-side-container {
  scrollbar-width: none;
}
#html {
  overflow-y: hidden !important;
}
/* thumb-label is absolutely positioned, so we will work with pixels here */
.time-radar-wrapper {
  background-color: white;
  border-bottom: 1px solid grey;
}
.sticky {
  position: sticky;
  top: 75px;
  max-height: 85vh;
}
#floor-plan-wrapper .row {
  height: 100%;
}
#floor-plan-wrapper .row .col {
  height: 100%;
}
#floor-plan-wrapper .column-content-card {
  max-height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
}
#floor-plan-header {
  position: sticky;
  top: 0px;
  z-index: 3;
}
#fab-activator-button::before {
  background: none;
}
</style>
